import { Show, createSignal, createUniqueId } from "solid-js";
import { Portal } from "solid-js/web";
import { Button } from "./Button";

export interface ConfirmOptions {
    title: string;
    description?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    /** "danger" pinta o botão confirm em vermelho. Default "primary" laranja. */
    tone?: "primary" | "danger";
}

interface InternalState {
    open: boolean;
    options: ConfirmOptions;
    resolve: (ok: boolean) => void;
    lastFocused: Element | null;
}

const [state, setState] = createSignal<InternalState | null>(null);

/**
 * Substitui window.confirm com diálogo modal customizado, branded.
 * Uso:
 *   if (!(await confirmDialog({ title: "Apagar?", tone: "danger" }))) return;
 */
export function confirmDialog(options: ConfirmOptions): Promise<boolean> {
    return new Promise((resolve) => {
        setState({
            open: true,
            options,
            resolve,
            lastFocused: typeof document !== "undefined" ? document.activeElement : null,
        });
    });
}

export function ConfirmDialogHost() {
    let confirmBtnRef: HTMLButtonElement | undefined;

    function handle(ok: boolean) {
        const s = state();
        if (!s) return;
        s.resolve(ok);
        setState(null);
        if (s.lastFocused instanceof HTMLElement) s.lastFocused.focus();
    }

    function handleKey(e: KeyboardEvent) {
        if (e.key === "Escape") {
            e.preventDefault();
            handle(false);
        }
    }

    return (
        <Show when={state()}>
            {(s) => {
                const titleId = createUniqueId();
                const descId = createUniqueId();
                queueMicrotask(() => confirmBtnRef?.focus());
                return (
                    <Portal>
                        <div
                            class="ui-modal-overlay"
                            onClick={(e) => {
                                if (e.target === e.currentTarget) handle(false);
                            }}
                            onKeyDown={handleKey}
                            role="presentation"
                        >
                            <div
                                class="ui-modal"
                                role="dialog"
                                aria-modal="true"
                                aria-labelledby={titleId}
                                aria-describedby={s().options.description ? descId : undefined}
                            >
                                <h2 id={titleId} class="ui-modal-title">
                                    {s().options.title}
                                </h2>
                                <Show when={s().options.description}>
                                    <p id={descId} class="ui-modal-desc">
                                        {s().options.description}
                                    </p>
                                </Show>
                                <div class="ui-modal-actions">
                                    <Button variant="ghost" size="md" onClick={() => handle(false)}>
                                        {s().options.cancelLabel ?? "Cancelar"}
                                    </Button>
                                    <Button
                                        ref={confirmBtnRef}
                                        variant={s().options.tone === "danger" ? "destructive" : "primary"}
                                        size="md"
                                        onClick={() => handle(true)}
                                    >
                                        {s().options.confirmLabel ?? "Confirmar"}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Portal>
                );
            }}
        </Show>
    );
}
